import axios from 'axios';
import {
  BangaloreNames,
  BangloreLocationBias,
  ChennaiLocationBias,
  ChennaiNames,
  DelhiLocationBias,
  DelhiNames,
  HyderabadNames,
  HydLocationBias,
  LOCAL_STORAGE_DATA,
  MumbaiLocationBias,
  MumbaiNames,
} from '../constants/app-constants';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import apis from '../Services/api-services';
import { modalActions } from '../Store/modalSlice';
import { proBenefits } from '../constants/app-constants';
import { userActions } from '../Store/userSlice';


const API_URL = process.env.REACT_APP_API_BASE_URL;

export const getAccessToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_DATA.ACCESS_TOKEN);
};

export const getRefreshToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_DATA.REFRESH_TOKEN);
};

export const getUserData = () => {
  const userDetails = localStorage.getItem(LOCAL_STORAGE_DATA.USER);
  if (userDetails) {
    return JSON.parse(userDetails);
  } else return expireSession();
};

export const expireSession = () => {
  localStorage.clear();
  window.location.replace('/');
  return null;
};

export const getInitials = (name) => {
  if (name) {
    const words = name.split(' ');
    const initials = words
      .slice(0, 1)
      .map((word) => word.charAt(0).toUpperCase());
    return initials.join(' ');
  }
  return '';
};

export const numDifferentiation = (val, isSqFt = false) => {
  let formatted = '';
  if (typeof val === 'string') {
    val = Number(val);
  }
  if (val === null) {
    return '';
  }
  if (val >= 10000000) {
    formatted = `${!isSqFt ? '₹' : ''} ${parseFloat(
      (val / 10000000).toFixed(2)
    )} Cr`;
  } else if (val >= 100000) {
    formatted = `${!isSqFt ? '₹' : ''} ${parseFloat(
      (val / 100000).toFixed(2)
    )} Lac`;
  } else if (val >= 1000) {
    formatted = `${!isSqFt ? '₹' : ''} ${!isSqFt ? parseFloat((val / 1000).toFixed(2)) + ' K' : val
      } `;
  }

  return formatted;
};

export const getUUID = () => {
  const newUUID = uuidv4();
  return newUUID;
}

export const createQueryURl = (params) => {
  const query = Object.keys(params)
    .map((key) => {
      const value = params[key];

      if (Array.isArray(value)) {
        return value
          .map(
            (item) => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`
          )
          .join('&');
      }

      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');

  return `?${query}`;
};

export const sanitizeData = (data) => {
  if (data === undefined || data === null) {
    return false;
  }

  if (Array.isArray(data)) {
    return data.length > 0;
  }

  if (typeof data === 'number') {
    return data !== 0;
  }

  if (data instanceof Date) {
    return !isNaN(data.getTime());
  }

  if (typeof data === 'object' && data.isDayjsObject) {
    return data.isValid();
  }

  return data !== '' && data !== 'null';
};

export const renderAreaOfOperatioChips = (data) => {
  if (data) {
    try {
      let parsedData = JSON.parse(JSON.parse(data || null));

      return parsedData[0].places || [];
    } catch (err) {
      console.log(err);
      return [];
    }
  } else {
    return [];
  }
};

export const fetchRefreshToken = async function () {
  try {
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_DATA.REFRESH_TOKEN);
    if (!refreshToken) {
      console.error('No refresh token found. Logging out...');
      expireSession();
      return null;
    }

    const response = await axios.post(
      `${API_URL}v1/users/refresh_token`,
      {},
      {
        headers: {
          'Grant-Type': 'refresh_token',
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    if (response.status === 200) {
      const newAccessToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;
      localStorage.setItem(LOCAL_STORAGE_DATA.ACCESS_TOKEN, newAccessToken);
      localStorage.setItem(LOCAL_STORAGE_DATA.REFRESH_TOKEN, newRefreshToken);
      console.log('Access token refreshed');
      return newAccessToken;
    } else {
      console.error('Failed to refresh token, logging out...');
      expireSession();
      return null;
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    expireSession();
    return null;
  }
};

export const isEmptyString = (str) => {
  return typeof str === 'string' && str.trim().length === 0;
};

export const filterByLocations = (predictions, _locations) => {
  if (!Array.isArray(predictions)) return [];

  const isKarnataka = (place) => {
    const desc = (place?.description || '').toLowerCase();
    if (desc.includes('karnataka')) return true;
    const terms = Array.isArray(place?.terms) ? place.terms : [];
    return terms.some((t) => (t?.value || '').toLowerCase().includes('karnataka'));
  };

  // Radius constraint (40km) is enforced upstream via BangloreLocationBias.radius = 40000
  // AutocompleteService respects location bias; here we only hard-filter to Karnataka.
  return predictions.filter(isKarnataka);
};

export const getLocationBiasByCity = (city) => {
  switch (city.toLowerCase()) {
    case 'bengaluru':
      return BangloreLocationBias;

    case 'delhi':
      return DelhiLocationBias;

    case 'chennai':
      return ChennaiLocationBias;

    case 'hyderabad':
      return HydLocationBias;

    case 'mumbai':
      return MumbaiLocationBias;

    default:
      return BangloreLocationBias;
  }
};

export const getFilteredAreaByCity = (city, predictions) => {
  switch (city.toLowerCase()) {
    case 'bengaluru':
      return filterByLocations(predictions, BangaloreNames);

    case 'delhi':
      return filterByLocations(predictions, DelhiNames);

    case 'chennai':
      return filterByLocations(predictions, ChennaiNames);

    case 'hyderabad':
      return filterByLocations(predictions, HyderabadNames);

    case 'mumbai':
      return filterByLocations(predictions, MumbaiNames);

    default:
      return filterByLocations(predictions, BangaloreNames);
  }
};

export const calculateAge = (year) => {
  const currentYear = new Date().getFullYear();
  return currentYear - year;
};

export const enableMarketingFeature = (created_at) => {
  const givenDate = dayjs(created_at);
  const startDate = dayjs('2025-01-02T11:52:03.521Z'); // End of April 4th
  return givenDate.isAfter(startDate);
};

export const formatBHKList = (bedrooms) => {
  if (!Array.isArray(bedrooms) || bedrooms.length === 0) return '';

  const bhks = bedrooms.filter((b) => b !== 'Studio');

  if (bhks.length === 0) return '';

  const bhkList = bhks.map((b) => `${b}BHK`);

  if (bhkList.length === 1) {
    return bhkList[0];
  }

  const last = bhkList.pop();
  return bhkList.join(', ') + ' & ' + last;
};
export const getDateRange = (period) => {
  const to_date = dayjs().format('YYYY-MM-DD');
  let from_date;
  switch (period) {
    case 'All':
      from_date = dayjs('2024-10-01').format('YYYY-MM-DD');
      break;
    case 'This month':
      from_date = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
      break;
    case 'This week':
      from_date = dayjs().subtract(1, 'week').format('YYYY-MM-DD');
      break;
    case 'Today':
      from_date = to_date;
      break;
    default:
      from_date = dayjs('2024-10-01').format('YYYY-MM-DD');
  }
  return { createdFromDate: from_date, createdToDate: to_date };
};

export const fetchAgentProfileStatus = async () => {
  try {
    const { data: { profile } = {} } = await apis.agent.getAgentProfileCompletionStatus();
    return profile || null;
  } catch (error) {
    console.error('Failed to fetch agent profile status:', error);
    return null;
  }
};

export const isPaymentEnabled = async () => {
  try {
    const profile = await fetchAgentProfileStatus();
    return profile?.is_payment_enabled || false;
  } catch (error) {
    console.error('Failed to check payment enabled status:', error);
    return false;
  }
};


export function isPastDate(dateString) {
  const today = dayjs().startOf("day");
  const givenDate = dayjs(dateString).startOf("day");
  return givenDate.isBefore(today);
}


export const handlePayment = async (dispatch, customSuccessItems = null, onTryAgain = null) => {
  try {
    // Check if payment is enabled
    const paymentEnabled = await isPaymentEnabled();
    if (!paymentEnabled) {
      console.log('Payment is disabled for this user');
      return;
    }

    // Fetch subscription dropdowns to get product ID
    const subscriptionRes = await apis.subscription.subscriptionDropdowns();
    const productID = subscriptionRes?.data?.subscription_types[0]?.id;

    if (!productID) {
      throw new Error('Product ID not found');
    }

    const res = await apis.subscription.payment({ product_id: productID });
    const payRes = res?.data?.data;

    const options = {
      key: payRes?.key_id,
      currency: payRes?.currency,
      name: "vREfyd",
      order_id: payRes?.rzp_order_id,
      handler: async function (response) {
        try {
          // Show loading while verifying payment status
          dispatch(
            modalActions.openPaymentStatus({
              status: 'loading',
              title: 'Verifying Payment',
              subtitle: 'Please wait while we verify your payment status...',
            })
          );

          const paymentStatus = await apis.subscription.paymentStatusVerify(response);

          const isPaymentSuccessful = paymentStatus?.data?.data?.status === 'paid' && paymentStatus?.data?.data?.success;
          if (isPaymentSuccessful) {
            // Show loading while updating subscription status
            dispatch(
              modalActions.openPaymentStatus({
                status: 'loading',
                title: 'Activating Subscription',
                subtitle: 'Please wait while we activate your subscription...',
              })
            );

            // Update subscription status in Redux store when payment is successful
            const profile = await fetchAgentProfileStatus();
            if (profile) {
              const {
                is_subscription_active,
                is_trial_subscription_used,
                subscription_end_date,
              } = profile;
              const isSubscriptionExpired = isPastDate(subscription_end_date);
              const isSubscriptionActive =
                is_subscription_active && !isSubscriptionExpired;
              const isTrialUsed = is_trial_subscription_used;

              dispatch(
                userActions.setSubscriptionStatus({
                  isSubscriptionActive,
                  isTrialUsed,
                  isSubscriptionExpired,
                  canViewData: isSubscriptionActive,
                  shouldShowFreeTrial: !isTrialUsed && !isSubscriptionActive,
                  shouldShowPaymentModal: isTrialUsed && !isSubscriptionActive,
                })
              );
            }

            // Show success modal
            dispatch(
              modalActions.openPaymentStatus({
                status: 'success',
                items: customSuccessItems || proBenefits,
                title: 'Payment successful',
                subtitle: 'Your subscription is now active.',
              })
            );
          } else {
            // Show failure modal
            dispatch(
              modalActions.openPaymentStatus({
                status: 'failure',
                title: 'Payment unsuccessful',
                subtitle: 'We couldn\'t process your payment.',
                onTryAgain: onTryAgain || (() => handlePayment(dispatch, customSuccessItems, onTryAgain))
              })
            );
          }
        } catch (error) {
          console.error('Error in payment handler:', error);
          // Show failure modal on error
          dispatch(
            modalActions.openPaymentStatus({
              status: 'failure',
              title: 'Payment verification failed',
              subtitle: 'There was an error verifying your payment. Please try again.',
              onTryAgain: onTryAgain || (() => handlePayment(dispatch, customSuccessItems, onTryAgain))
            })
          );
        }
      },
      theme: {
        color: "#3399cc",
      },
      // prefill: {
      //   name: 'John Doe',
      //   email: 'john@example.com',   // required
      //   contact: '9686463519'        // required
      // }
    };

    const rzp = new window.Razorpay(options);

    rzp.on('payment.failed', function () {
      dispatch(
        modalActions.openPaymentStatus({
          status: 'failure',
          title: 'Payment unsuccessful',
          subtitle: 'We couldn\'t process your payment.',
          onTryAgain: onTryAgain || (() => handlePayment(dispatch, customSuccessItems, onTryAgain))
        })
      );
    });

    rzp.open();
  } catch (error) {
    console.error('Payment initialization failed:', error);
    dispatch(
      modalActions.openPaymentStatus({
        status: 'failure',
        title: 'Payment unsuccessful',
        subtitle: 'We couldn\'t process your payment.',
        onTryAgain: onTryAgain || (() => handlePayment(dispatch, customSuccessItems, onTryAgain))
      })
    );
  }
};
